/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { LoggerStore } from "../../logger/logger-store"
import type { Result } from "../../network/http-client"
import { makeDeleteRequest, makeGetRequest, makePostRequest, makePutRequest } from "../../network/http-client"

import type { UserDetailStore } from "./user-detail-store"
import type { UserCreate, UserDetail, UserRole, UserUpdate } from "./user-types"

enum Endpoint {
    user = "/api/private/user"
}

export class UserDetailService {
    constructor(
        private readonly store: UserDetailStore,
        private readonly logger: LoggerStore
    ) {}

    async getOrganizationUser(userId: number): Promise<Result<UserDetail>> {
        const user = await makeGetRequest<UserDetail>(`${Endpoint.user}/${userId}`, {})
        if (user.success && user.data) {
            this.store.setUserDetail(user.data)
        }
        if (!user.success) {
            this.logger.logApiError("Get organization user failed", user.apiError, user.error)
        }
        return user
    }

    async createOrganizationUser(
        name: string,
        username: string,
        organizationId: number,
        roles: UserRole[],
        applicationTypeIds: number[]
    ): Promise<Result<UserDetail>> {
        const user = await makePostRequest<UserCreate, UserDetail>(
            `${Endpoint.user}`,
            {},
            { name, username, organizationId, roles, applicationTypeIds }
        )
        if (user.success && user.data) {
            this.logger.logOk("User created successfully")
        }
        if (!user.success) {
            this.logger.logApiError("Create organization user failed", user.apiError, user.error)
        }
        return user
    }

    async updateOrganizationUser(id: number, name: string): Promise<Result<UserDetail>> {
        const user = await makePutRequest<UserUpdate, UserDetail>(`${Endpoint.user}/${id}`, {}, { name })
        if (user.success && user.data) {
            this.logger.logOk("User updated successfully")
            this.store.setUserDetail(user.data)
        }
        if (!user.success) {
            this.logger.logApiError("Update organization user failed", user.apiError, user.error)
        }
        return user
    }

    async deleteOrganizationUser(id: number): Promise<Result<void>> {
        const res = await makeDeleteRequest<void>(`${Endpoint.user}/${id}`, {})
        if (res.success) {
            this.logger.logOk("User deleted successfully")
        }
        if (!res.success) {
            this.logger.logApiError("Delete organization user failed", res.apiError, res.error)
        }
        return res
    }

    async resetUserPassword(userId: number): Promise<Result<void>> {
        const res = await makePostRequest<never, void>(`${Endpoint.user}/${userId}/reset-password`, {})
        if (res.success) {
            this.logger.logOk("User password reset successfully")
        }
        if (!res.success) {
            this.logger.logApiError("User password reset failed", res.apiError, res.error)
        }
        return res
    }
}
